// src/pages/DeletedItems.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Tooltip,
  Alert,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  getDoc,
  writeBatch,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { warn, error, debug } from "../logger";

type Row = {
  id: string;
  title: string;
  itemType: "survey" | "list";
  updatedAt?: Date | null;
  userId?: string; // owner uid (needed for row-level permissions)
};

export default function DeletedItems() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [showAll, setShowAll] = useState<boolean>(false); // admin scope toggle
  const navigate = useNavigate();

  // Load accountRole once (determine admin)
  useEffect(() => {
    const loadRole = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        const role = snap.exists() ? (snap.data() as any)?.accountRole : undefined;
        setIsAdmin(role === "admin");
      } catch (e) {
        debug("DeletedItems: failed to load accountRole; defaulting to non-admin.", e);
        setIsAdmin(false);
      }
    };
    loadRole();
  }, []);

  // Fetch deleted items, role-aware
  useEffect(() => {
    async function fetchDeleted() {
      const user = auth.currentUser;
      if (!user) {
        warn("DeletedItems: no authenticated user");
        setRows([]);
        setLoading(false);
        return;
      }

      setErrMsg(null);
      setLoading(true);

      try {
        const fetchDeletedFromCollection = async (
          collectionName: "surveys" | "lists",
          itemType: "survey" | "list"
        ): Promise<Row[]> => {
          const toRow = (d: any): Row => {
            const v = d.data() as any;
            const ownerUid = (v.ownerId ?? v.userId) as string | undefined;
            const fallbackTitle = itemType === "survey" ? "(Untitled survey)" : "(Untitled list)";
            return {
              id: d.id,
              title: (v.title || v.name || fallbackTitle) as string,
              itemType,
              updatedAt: v.updatedAt?.toDate?.() ?? null,
              userId: ownerUid,
            };
          };

          if (isAdmin && showAll) {
            try {
              const qAll = query(
                collection(db, collectionName),
                where("status", "==", "deleted"),
                orderBy("updatedAt", "desc")
              );
              const snap = await getDocs(qAll);
              return snap.docs.map(toRow);
            } catch {
              const qAllNoOrder = query(
                collection(db, collectionName),
                where("status", "==", "deleted")
              );
              const snap = await getDocs(qAllNoOrder);
              return snap.docs.map(toRow);
            }
          }

          try {
            const qOwn = query(
              collection(db, collectionName),
              where("userId", "==", user.uid),
              where("status", "==", "deleted"),
              orderBy("updatedAt", "desc")
            );
            const snap = await getDocs(qOwn);
            return snap.docs.map(toRow);
          } catch {
            const qOwnNoOrder = query(
              collection(db, collectionName),
              where("userId", "==", user.uid),
              where("status", "==", "deleted")
            );
            const snap = await getDocs(qOwnNoOrder);
            return snap.docs.map(toRow);
          }
        };

        const [deletedSurveys, deletedLists] = await Promise.all([
          fetchDeletedFromCollection("surveys", "survey"),
          fetchDeletedFromCollection("lists", "list"),
        ]);

        const items = [...deletedSurveys, ...deletedLists].sort((a, b) => {
          const ta = a.updatedAt?.getTime?.() ?? 0;
          const tb = b.updatedAt?.getTime?.() ?? 0;
          return tb - ta;
        });

        setRows(items);
      } catch (e: any) {
        error("DeletedItems: fetch failed", e);
        setErrMsg(e?.message || "Failed to load deleted items.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    }

    fetchDeleted();
  }, [isAdmin, showAll]);

  // Row-level action guard
  const canActOnRow = useMemo(() => {
    const uid = auth.currentUser?.uid || "";
    return (row: Row) => {
      if (!row.userId) return false;
      if (row.itemType === "survey") return isAdmin || row.userId === uid;
      return row.userId === uid; // list rules currently require owner
    };
  }, [isAdmin]);

  const restore = async (row: Row) => {
    try {
      if (row.itemType === "survey") {
        await updateDoc(doc(db, "surveys", row.id), {
          status: "unpublished",
          published: false, // legacy boolean kept tidy
          updatedAt: serverTimestamp(),
        });
      } else {
        await updateDoc(doc(db, "lists", row.id), {
          status: "active",
          updatedAt: serverTimestamp(),
        });
      }
      setRows((prev) => prev.filter((r) => !(r.id === row.id && r.itemType === row.itemType)));
    } catch (e) {
      error("DeletedItems: restore failed", e);
      setErrMsg(`Failed to restore ${row.itemType}.`);
    }
  };

  const hardDelete = async (row: Row) => {
    const confirmMsg =
      row.itemType === "survey"
        ? "Permanently delete this survey document? (Responses subcollection is NOT removed.)"
        : "Permanently delete this list and all list items?";
    if (!window.confirm(confirmMsg)) return;

    try {
      if (row.itemType === "survey") {
        await deleteDoc(doc(db, "surveys", row.id));
      } else {
        const itemsCol = collection(db, "lists", row.id, "items");
        const itemsSnap = await getDocs(itemsCol);
        if (!itemsSnap.empty) {
          let batch = writeBatch(db);
          let opCount = 0;
          for (const item of itemsSnap.docs) {
            batch.delete(item.ref);
            opCount += 1;
            if (opCount >= 450) {
              await batch.commit();
              batch = writeBatch(db);
              opCount = 0;
            }
          }
          if (opCount > 0) await batch.commit();
        }
        await deleteDoc(doc(db, "lists", row.id));
      }

      setRows((prev) => prev.filter((r) => !(r.id === row.id && r.itemType === row.itemType)));
    } catch (e) {
      error("DeletedItems: hard delete failed", e);
      setErrMsg(`Failed to permanently delete ${row.itemType}.`);
    }
  };

  const columns: GridColDef<Row>[] = [
    { field: "title", headerName: "Title", flex: 1, minWidth: 220 },
    {
      field: "itemType",
      headerName: "Type",
      width: 110,
      renderCell: ({ row }) => (row.itemType === "survey" ? "Survey" : "List"),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      width: 180,
      renderCell: ({ row }) =>
        row.updatedAt ? (row.updatedAt as Date).toLocaleString() : "",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 170,
      getActions: (params) => {
        const row = params.row as Row;
        const allowed = canActOnRow(row);
        return [
          <GridActionsCellItem
            key="restore"
            label="Restore"
            icon={<RestoreFromTrashIcon />}
            onClick={() => restore(row)}
            showInMenu
            disabled={!allowed}
          />,
          <GridActionsCellItem
            key="hardDelete"
            label="Hard Delete"
            icon={<DeleteForeverIcon />}
            onClick={() => hardDelete(row)}
            showInMenu
            disabled={!allowed}
          />,
        ];
      },
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      {errMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errMsg}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6">Deleted Items</Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {isAdmin && (
            <FormControlLabel
              control={
                <Switch
                  checked={showAll}
                  onChange={(_, v) => setShowAll(v)}
                />
              }
              label="Show all (admin)"
            />
          )}
          <Tooltip title="Back to Surveys">
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/surveys")}
            >
              Back
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ width: "100%", overflowX: "auto", mt: 1 }}>
        <DataGrid
          loading={loading}
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          pageSizeOptions={[5, 10, 20]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pagination
          autoHeight
          sx={{
            "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
          }}
        />
      </Box>
    </Box>
  );
}
